import { Link, graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import PropTypes from 'prop-types';
import usdaLogoPath1x from '../images/usda-logo-reverse@1x.png';
import usdaLogoPath2x from '../images/usda-logo-reverse@2x.png';
import logo1x from '../images/logo@1x-reverse.png';
import logo2x from '../images/logo@2x-reverse.png';

const SocialLink = ({ service }) => (
  <a
    href={service.link}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={service.icon_alt_text}
  >
    <i className={`fi-social-${service.name}`} aria-hidden="true" />
  </a>
);

const FooterLink = ({ linkData }) => (
  <li>
    {linkData.type === 'internal' && (
      <Link to={linkData.link}>{linkData.text}</Link>
    )}
    {linkData.type === 'external' && (
      <a href={linkData.link} target="_blank" rel="noopener noreferrer">
        {linkData.text}
      </a>
    )}
    {linkData.type === 'email' && <a href={linkData.link}>{linkData.text}</a>}
  </li>
);

const FooterMarkup = ({ componentText }) => {
  const { frontmatter } = componentText;
  return (
    <footer>
      <div className="footer--newsletter-bar">
        <div className="grid-container">
          <div className="grid-x grid-padding-y">
            <div className="footer--newsletter-bar__content cell small-12">
              <p>
                <i className="fi-mail" aria-hidden="true"></i>{' '}
                <a href="https://admailemail.com/form/12641/1218/" target="_blank" rel="noopener noreferrer">
                  Sign up for our newsletter to get garden tips and be notified
                  of sales!
                </a>{' '}
                <em>We'll never share, sell, or spam your email.</em>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="grid-container">
        <div className="grid-x">
          <div className="cell small-12 medium-6 footer--logo-container">
            <img
              src={usdaLogoPath1x}
              srcSet={`${usdaLogoPath2x} 2x`}
              alt="USDA certified organic logo"
              width="100"
            />
            <img
              src={logo1x}
              srcSet={`${logo2x} 2x`}
              alt="Organic plants from Mountain Valley Growers"
              width="200"
            />
          </div>
          <div className="cell small-12 medium-6">
            <div className="footer--social">
              {frontmatter.social.map((service) => (
                <SocialLink service={service} key={service.name} />
              ))}
            </div>
            <p className="header__small-caps">{frontmatter.link_heading}</p>
            <ul>
              {frontmatter.links.map((link, index) => (
                <FooterLink linkData={link} key={index} />
              ))}
            </ul>
          </div>
          <div className="cell small-12 footer--tagline">
            <p>
              <small>
                Growing USDA Certified Organic Herbs, Perennials, and Vegetables
                since 1985.
              </small>
            </p>
          </div>
        </div>
      </div>
      <div className="footer--copyright">
        © 1997 - {new Date().getFullYear()}, Mountain Valley Growers, Inc.
      </div>
      <span
        itemProp="brand"
        itemType="https://schema.org/Organization"
        itemScope
        id="mvg-org"
      >
        <meta itemProp="url" content="https://mountainvalleygrowers.com" />
        <meta itemProp="name" content="Mountain Valley Growers" />
        <link itemProp="sameAs" href="https://www.pinterest.com/mvgorganic/" />
        <link
          itemProp="sameAs"
          href="https://www.instagram.com/mountainvalleygrowers/"
        />
      </span>

      <span
        itemProp="seller"
        itemType="https://schema.org/Organization"
        itemScope
        id="plugtrays-org"
      >
        <link itemProp="additionalType" href="http://schema.org/WebSite" />
        <meta itemProp="url" content="https://plugtrays.com" />
        <meta itemProp="name" content="Organic Plug Trays" />
        <link itemProp="sameAs" href="https://www.pinterest.com/mvgorganic/" />
        <link
          itemProp="sameAs"
          href="https://www.instagram.com/mountainvalleygrowers/"
        />
      </span>
    </footer>
  );
};

const Footer = () => {
  const data = useStaticQuery(graphql`
    query Footer {
      allMdx(filter: { frontmatter: { component: { eq: "footer" } } }) {
        edges {
          node {
            body
            frontmatter {
              component
              social {
                name
                link
                icon_alt_text
              }
              link_heading
              links {
                text
                link
                type
              }
            }
          }
        }
      }
    }
  `);
  return <FooterMarkup componentText={data.allMdx.edges[0].node} />;
};

SocialLink.propTypes = {
  service: PropTypes.shape({
    name: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    icon_alt_text: PropTypes.string.isRequired,
  }).isRequired,
};

FooterLink.propTypes = {
  linkData: PropTypes.shape({
    text: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
};

FooterMarkup.propTypes = {
  componentText: PropTypes.shape({
    body: PropTypes.string.isRequired,
    frontmatter: PropTypes.shape({
      component: PropTypes.string.isRequired,
      social: PropTypes.any.isRequired,
      link_heading: PropTypes.string.isRequired,
      links: PropTypes.any.isRequired,
    }).isRequired,
  }).isRequired,
};

export default Footer;
